import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Plus, Search, FileText, Video, Image as ImageIcon, Download, Eye, Edit, Trash2, BookOpen, CheckCircle, Clock, FolderOpen, Layers, Target, Users, Calendar, ChevronRight, UploadCloud, File, Link as LinkIcon } from 'lucide-react';
// Types
interface Program {
  id: string;
  name: string;
  code: string;
  description: string;
  status: 'active' | 'draft' | 'archived';
  priority: 'high' | 'medium' | 'low';
  startDate: string;
  endDate?: string;
  targetRegions: number;
  activeRegions: number;
  toolkitCount: number;
  sopCount: number;
  lastUpdated: string;
}
interface Toolkit {
  id: string;
  name: string;
  type: 'sop' | 'poster' | 'video' | 'infografis' | 'template' | 'panduan';
  programId: string;
  version: string;
  downloads: number;
  lastUpdated: string;
}
// Mock Data
const mockPrograms: Program[] = [{
  id: '1',
  name: 'Pilah Sampah dari Rumah',
  code: 'PSR-2024',
  description: 'Program nasional pemilahan sampah organik dan anorganik di tingkat rumah tangga.',
  status: 'active',
  priority: 'high',
  startDate: '2024-01-01',
  targetRegions: 514,
  activeRegions: 420,
  toolkitCount: 12,
  sopCount: 5,
  lastUpdated: '2024-02-15'
}, {
  id: '2',
  name: 'Cegah Sampah Plastik',
  code: 'CSP-2024',
  description: 'Kampanye pengurangan penggunaan plastik sekali pakai di pasar tradisional dan ritel.',
  status: 'active',
  priority: 'high',
  startDate: '2024-01-15',
  targetRegions: 514,
  activeRegions: 380,
  toolkitCount: 8,
  sopCount: 3,
  lastUpdated: '2024-02-10'
}, {
  id: '3',
  name: 'HPSN 2024',
  code: 'HPSN-2024',
  description: 'Hari Peduli Sampah Nasional 2024 - Atasi Sampah Plastik dengan Cara Produktif.',
  status: 'active',
  priority: 'high',
  startDate: '2024-02-21',
  endDate: '2024-02-21',
  targetRegions: 514,
  activeRegions: 514,
  toolkitCount: 15,
  sopCount: 8,
  lastUpdated: '2024-02-20'
}, {
  id: '4',
  name: 'Ekonomi Sirkular',
  code: 'ES-2024',
  description: 'Membangun rantai nilai ekonomi dari pengelolaan sampah yang berkelanjutan.',
  status: 'active',
  priority: 'medium',
  startDate: '2024-03-01',
  targetRegions: 200,
  activeRegions: 85,
  toolkitCount: 6,
  sopCount: 4,
  lastUpdated: '2024-02-18'
}, {
  id: '5',
  name: 'Ramadhan Minim Sampah',
  code: 'RMS-2024',
  description: 'Gerakan nasional mengurangi sampah makanan dan kemasan selama bulan Ramadhan.',
  status: 'draft',
  priority: 'medium',
  startDate: '2024-03-10',
  endDate: '2024-04-10',
  targetRegions: 514,
  activeRegions: 0,
  toolkitCount: 4,
  sopCount: 2,
  lastUpdated: '2024-02-19'
}];
const mockToolkits: Toolkit[] = [{
  id: '1',
  name: 'SOP Sosialisasi Pilah Sampah RT/RW',
  type: 'sop',
  programId: '1',
  version: '2.1',
  downloads: 1250,
  lastUpdated: '2024-02-15'
}, {
  id: '2',
  name: 'Poster Pilah Sampah 5 Jenis',
  type: 'poster',
  programId: '1',
  version: '1.0',
  downloads: 3200,
  lastUpdated: '2024-01-20'
}, {
  id: '3',
  name: 'Video Tutorial Komposting',
  type: 'video',
  programId: '1',
  version: '1.2',
  downloads: 890,
  lastUpdated: '2024-02-01'
}, {
  id: '4',
  name: 'Infografis Alur Bank Sampah',
  type: 'infografis',
  programId: '1',
  version: '1.1',
  downloads: 2100,
  lastUpdated: '2024-01-25'
}, {
  id: '5',
  name: 'Template Press Release HPSN',
  type: 'template',
  programId: '3',
  version: '1.0',
  downloads: 450,
  lastUpdated: '2024-02-18'
}, {
  id: '6',
  name: 'Panduan Teknis Aksi Bersih',
  type: 'panduan',
  programId: '3',
  version: '3.0',
  downloads: 780,
  lastUpdated: '2024-02-20'
}];
export function KatalogProgramPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isToolkitModalOpen, setIsToolkitModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('programs');
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="hijau" data-id="element-1511">Aktif</StatusBadge>;
      case 'draft':
        return <StatusBadge status="kuning" data-id="element-1512">Draft</StatusBadge>;
      case 'archived':
        return <StatusBadge status="abu" data-id="element-1513">Arsip</StatusBadge>;
      default:
        return null;
    }
  };
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <Badge className="bg-red-100 text-red-700 border-red-200" data-id="element-1514">
            Prioritas Tinggi
          </Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200" data-id="element-1515">
            Prioritas Sedang
          </Badge>;
      case 'low':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-id="element-1516">
            Prioritas Rendah
          </Badge>;
      default:
        return null;
    }
  };
  const getToolkitIcon = (type: string) => {
    switch (type) {
      case 'sop':
        return <FileText className="h-4 w-4 text-blue-500" data-id="element-1517" />;
      case 'poster':
        return <ImageIcon className="h-4 w-4 text-green-500" data-id="element-1518" />;
      case 'video':
        return <Video className="h-4 w-4 text-red-500" data-id="element-1519" />;
      case 'infografis':
        return <ImageIcon className="h-4 w-4 text-purple-500" data-id="element-1520" />;
      case 'template':
        return <File className="h-4 w-4 text-orange-500" data-id="element-1521" />;
      case 'panduan':
        return <BookOpen className="h-4 w-4 text-teal-500" data-id="element-1522" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" data-id="element-1523" />;
    }
  };
  const programToolkits = selectedProgram ? mockToolkits.filter(t => t.programId === selectedProgram.id) : [];
  return <CommandCenterLayout title="Katalog Program KIE Nasional" subtitle="Single Source of Truth - Program prioritas, SOP, dan toolkit implementasi standar" data-id="element-1524">
      <div className="space-y-6" data-id="element-1525">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1526">
          <Card data-id="element-1527">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-1528">
              <div className="p-3 bg-primary-100 rounded-full text-primary-600" data-id="element-1529">
                <Layers className="h-6 w-6" data-id="element-1530" />
              </div>
              <div data-id="element-1531">
                <p className="text-sm text-gray-500" data-id="element-1532">Total Program</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1533">12</p>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1534">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-1535">
              <div className="p-3 bg-green-100 rounded-full text-green-600" data-id="element-1536">
                <CheckCircle className="h-6 w-6" data-id="element-1537" />
              </div>
              <div data-id="element-1538">
                <p className="text-sm text-gray-500" data-id="element-1539">Program Aktif</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1540">8</p>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1541">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-1542">
              <div className="p-3 bg-blue-100 rounded-full text-blue-600" data-id="element-1543">
                <FolderOpen className="h-6 w-6" data-id="element-1544" />
              </div>
              <div data-id="element-1545">
                <p className="text-sm text-gray-500" data-id="element-1546">Total Toolkit</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1547">156</p>
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1548">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-1549">
              <div className="p-3 bg-orange-100 rounded-full text-orange-600" data-id="element-1550">
                <Download className="h-6 w-6" data-id="element-1551" />
              </div>
              <div data-id="element-1552">
                <p className="text-sm text-gray-500" data-id="element-1553">Total Unduhan</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1554">45.2K</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card data-id="element-1555">
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 gap-4" data-id="element-1556">
            <div data-id="element-1557">
              <CardTitle data-id="element-1558">Daftar Program Prioritas</CardTitle>
              <CardDescription data-id="element-1559">
                Program KIE nasional dengan SOP dan toolkit terstandar
              </CardDescription>
            </div>
            <Button className="bg-primary-600 hover:bg-primary-700 text-white gap-2 w-full sm:w-auto" onClick={() => setIsCreateModalOpen(true)} data-id="element-1560">
              <Plus className="h-4 w-4" data-id="element-1561" />
              Tambah Program
            </Button>
          </CardHeader>
          <CardContent data-id="element-1562">
            {/* Search & Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-1563">
              <div className="relative flex-1" data-id="element-1564">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-1565" />
                <Input placeholder="Cari program..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1566" />
              </div>
              <div className="flex gap-2 w-full md:w-auto" data-id="element-1567">
                <Select className="flex-1 md:w-40" data-id="element-1568">
                  <option data-id="element-1569">Semua Status</option>
                  <option data-id="element-1570">Aktif</option>
                  <option data-id="element-1571">Draft</option>
                  <option data-id="element-1572">Arsip</option>
                </Select>
                <Select className="flex-1 md:w-40" data-id="element-1573">
                  <option data-id="element-1574">Semua Prioritas</option>
                  <option data-id="element-1575">Tinggi</option>
                  <option data-id="element-1576">Sedang</option>
                  <option data-id="element-1577">Rendah</option>
                </Select>
              </div>
            </div>

            {/* Program Cards */}
            <div className="space-y-4" data-id="element-1578">
              {mockPrograms.map(program => <div key={program.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedProgram(program)} data-id="element-1579">
                  <div className="flex items-start justify-between" data-id="element-1580">
                    <div className="flex-1" data-id="element-1581">
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2" data-id="element-1582">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-1583">
                          {program.name}
                        </h3>
                        {getStatusBadge(program.status)}
                        {getPriorityBadge(program.priority)}
                      </div>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-1584">
                        {program.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500" data-id="element-1585">
                        <span className="flex items-center gap-1" data-id="element-1586">
                          <Calendar className="h-4 w-4" data-id="element-1587" />
                          {program.startDate}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-1588">
                          <Target className="h-4 w-4" data-id="element-1589" />
                          {program.activeRegions}/{program.targetRegions} Daerah
                        </span>
                        <span className="flex items-center gap-1" data-id="element-1590">
                          <FolderOpen className="h-4 w-4" data-id="element-1591" />
                          {program.toolkitCount} Toolkit
                        </span>
                        <span className="flex items-center gap-1" data-id="element-1592">
                          <FileText className="h-4 w-4" data-id="element-1593" />
                          {program.sopCount} SOP
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2" data-id="element-1594">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hidden sm:flex" data-id="element-1595">
                        <Edit className="h-4 w-4 text-gray-400" data-id="element-1596" />
                      </Button>
                      <ChevronRight className="h-5 w-5 text-gray-400" data-id="element-1597" />
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4" data-id="element-1598">
                    <div className="flex justify-between text-xs text-gray-500 mb-1" data-id="element-1599">
                      <span data-id="element-1600">Adopsi Daerah</span>
                      <span data-id="element-1601">
                        {Math.round(program.activeRegions / program.targetRegions * 100)}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1602">
                      <div className="bg-primary-600 h-2 rounded-full transition-all" style={{
                    width: `${program.activeRegions / program.targetRegions * 100}%`
                  }} data-id="element-1603" />
                    </div>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Program Detail Modal */}
      <Modal isOpen={!!selectedProgram} onClose={() => setSelectedProgram(null)} title={selectedProgram?.name || 'Detail Program'} size="2xl" data-id="element-1604">
        {selectedProgram && <div className="space-y-6" data-id="element-1605">
            {/* Header Info */}
            <div className="flex flex-col sm:flex-row items-start justify-between p-4 bg-gray-50 rounded-lg border gap-4" data-id="element-1606">
              <div data-id="element-1607">
                <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1608">
                  <span className="font-mono text-sm text-gray-500" data-id="element-1609">
                    {selectedProgram.code}
                  </span>
                  {getStatusBadge(selectedProgram.status)}
                  {getPriorityBadge(selectedProgram.priority)}
                </div>
                <p className="text-gray-600" data-id="element-1610">{selectedProgram.description}</p>
              </div>
              <Button className="gap-2 w-full sm:w-auto" data-id="element-1611">
                <Edit className="h-4 w-4" data-id="element-1612" /> Edit Program
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-1613">
              <div className="text-center p-3 border rounded-lg" data-id="element-1614">
                <p className="text-2xl font-bold text-primary-600" data-id="element-1615">
                  {selectedProgram.activeRegions}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1616">Daerah Aktif</p>
              </div>
              <div className="text-center p-3 border rounded-lg" data-id="element-1617">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1618">
                  {selectedProgram.toolkitCount}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1619">Toolkit</p>
              </div>
              <div className="text-center p-3 border rounded-lg" data-id="element-1620">
                <p className="text-2xl font-bold text-gray-900" data-id="element-1621">
                  {selectedProgram.sopCount}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1622">SOP</p>
              </div>
              <div className="text-center p-3 border rounded-lg" data-id="element-1623">
                <p className="text-2xl font-bold text-green-600" data-id="element-1624">85%</p>
                <p className="text-xs text-gray-500" data-id="element-1625">Compliance</p>
              </div>
            </div>

            {/* Toolkit List */}
            <div data-id="element-1626">
              <div className="flex items-center justify-between mb-4" data-id="element-1627">
                <h4 className="font-medium text-gray-900" data-id="element-1628">Toolkit & Materi</h4>
                <Button size="sm" className="gap-2" onClick={() => setIsToolkitModalOpen(true)} data-id="element-1629">
                  <Plus className="h-4 w-4" data-id="element-1630" /> Tambah Toolkit
                </Button>
              </div>
              <div className="space-y-2" data-id="element-1631">
                {programToolkits.length > 0 ? programToolkits.map(toolkit => <div key={toolkit.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg hover:bg-gray-50 gap-3" data-id="element-1632">
                      <div className="flex items-center gap-3" data-id="element-1633">
                        <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0" data-id="element-1634">
                          {getToolkitIcon(toolkit.type)}
                        </div>
                        <div data-id="element-1635">
                          <p className="font-medium text-gray-900" data-id="element-1636">
                            {toolkit.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1637">
                            v{toolkit.version} •{' '}
                            {toolkit.downloads.toLocaleString()} unduhan
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 justify-end" data-id="element-1638">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-id="element-1639">
                          <Eye className="h-4 w-4" data-id="element-1640" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-id="element-1641">
                          <Download className="h-4 w-4" data-id="element-1642" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-id="element-1643">
                          <Edit className="h-4 w-4" data-id="element-1644" />
                        </Button>
                      </div>
                    </div>) : <div className="text-center py-8 text-gray-500" data-id="element-1645">
                    <FolderOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" data-id="element-1646" />
                    <p data-id="element-1647">Belum ada toolkit untuk program ini</p>
                  </div>}
              </div>
            </div>
          </div>}
      </Modal>

      {/* Create Program Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Tambah Program Baru" size="lg" footer={<div className="flex justify-end gap-2 w-full" data-id="element-1649">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)} data-id="element-1650">
              Batal
            </Button>
            <Button variant="outline" data-id="element-1651">Simpan Draft</Button>
            <Button className="bg-primary-600 text-white" data-id="element-1652">
              Publish Program
            </Button>
          </div>} data-id="element-1648">
        <div className="space-y-4" data-id="element-1653">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1654">
            <div className="col-span-1 sm:col-span-2" data-id="element-1655">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1656">
                Nama Program
              </label>
              <Input placeholder="Contoh: Pilah Sampah dari Rumah" data-id="element-1657" />
            </div>
            <div data-id="element-1658">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1659">
                Kode Program
              </label>
              <Input placeholder="PSR-2024" data-id="element-1660" />
            </div>
            <div data-id="element-1661">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1662">
                Prioritas
              </label>
              <Select data-id="element-1663">
                <option value="high" data-id="element-1664">Tinggi</option>
                <option value="medium" data-id="element-1665">Sedang</option>
                <option value="low" data-id="element-1666">Rendah</option>
              </Select>
            </div>
            <div data-id="element-1667">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1668">
                Tanggal Mulai
              </label>
              <Input type="date" data-id="element-1669" />
            </div>
            <div data-id="element-1670">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1671">
                Tanggal Selesai (Opsional)
              </label>
              <Input type="date" data-id="element-1672" />
            </div>
            <div className="col-span-1 sm:col-span-2" data-id="element-1673">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1674">
                Deskripsi
              </label>
              <Textarea placeholder="Jelaskan tujuan dan ruang lingkup program..." className="h-24" data-id="element-1675" />
            </div>
            <div className="col-span-1 sm:col-span-2" data-id="element-1676">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1677">
                Target Daerah
              </label>
              <Select data-id="element-1678">
                <option value="514" data-id="element-1679">Nasional (514 Kab/Kota)</option>
                <option value="custom" data-id="element-1680">Pilih Daerah Tertentu</option>
              </Select>
            </div>
          </div>
        </div>
      </Modal>

      {/* Add Toolkit Modal */}
      <Modal isOpen={isToolkitModalOpen} onClose={() => setIsToolkitModalOpen(false)} title="Tambah Toolkit" size="md" footer={<div className="flex justify-end gap-2 w-full" data-id="element-1682">
            <Button variant="outline" onClick={() => setIsToolkitModalOpen(false)} data-id="element-1683">
              Batal
            </Button>
            <Button className="bg-primary-600 text-white" data-id="element-1684">
              Upload Toolkit
            </Button>
          </div>} data-id="element-1681">
        <div className="space-y-4" data-id="element-1685">
          <div data-id="element-1686">
            <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1687">
              Nama Toolkit
            </label>
            <Input placeholder="Contoh: SOP Sosialisasi RT/RW" data-id="element-1688" />
          </div>
          <div data-id="element-1689">
            <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1690">
              Tipe
            </label>
            <Select data-id="element-1691">
              <option value="sop" data-id="element-1692">SOP / Panduan Teknis</option>
              <option value="poster" data-id="element-1693">Poster</option>
              <option value="video" data-id="element-1694">Video</option>
              <option value="infografis" data-id="element-1695">Infografis</option>
              <option value="template" data-id="element-1696">Template (Press Release, dll)</option>
              <option value="panduan" data-id="element-1697">Panduan Umum</option>
            </Select>
          </div>
          <div data-id="element-1698">
            <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1699">
              Versi
            </label>
            <Input placeholder="1.0" data-id="element-1700" />
          </div>
          <div data-id="element-1701">
            <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1702">
              Upload File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 cursor-pointer" data-id="element-1703">
              <UploadCloud className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-1704" />
              <p className="text-sm text-gray-600" data-id="element-1705">
                Klik untuk upload atau drag & drop
              </p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-1706">
                PDF, MP4, JPG, PNG (Max. 50MB)
              </p>
            </div>
          </div>
        </div>
      </Modal>
    </CommandCenterLayout>;
}